import { useCallback, useEffect, useState } from 'react';
import { Link, NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { api, tokens } from '@shared/api';
import { BRAND_NAME, BrandMark, MarkGlyph } from '@shared/brand';
import { idr } from '@shared/format';
import { useGatewayStatus, useNavDrawer } from '@shared/hooks';
import { Icon, ThemeToggle } from './components/ui';
import { NAV } from './nav';
import { SearchProvider } from './search/SearchProvider';
import { SearchTrigger } from './search/SearchTrigger';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Playground } from './pages/Playground';
import { Models } from './pages/Models';
import { ApiKeys } from './pages/ApiKeys';
import { Projects } from './pages/Projects';
import { ProjectDetail } from './pages/ProjectDetail';
import { Usage } from './pages/Usage';
import { Checkout } from './pages/Checkout';
import { Wallet } from './pages/Wallet';
import { Transactions } from './pages/Transactions';
import { Docs } from './pages/Docs';
import { Settings } from './pages/Settings';

export interface Session {
  user_id: string;
  email: string;
  platform_role: string;
  active_organization_id: string | null;
}

function useCrumb(): { section: string; label: string } {
  const { pathname } = useLocation();
  if (pathname.startsWith('/checkout/')) return { section: 'Tagihan', label: 'Pembayaran' };
  for (const group of NAV) {
    const hit = group.links.find((link) => pathname.startsWith(link.to));
    if (hit) return { section: group.section, label: hit.label };
  }
  return { section: '', label: '' };
}

function DemoBadge() {
  const { online, demo } = useGatewayStatus();
  if (online === false) {
    return (
      <span className="badge badge-critical" title="Gateway tidak bisa dihubungi">
        <span className="dot pulse" />
        Offline
      </span>
    );
  }
  if (!demo) return null;
  return (
    <span className="badge badge-warn" title="Model asli dijawab simulator. Tagihan tetap dihitung seperti produksi.">
      <span className="dot" />
      Mode demo
    </span>
  );
}

function BalanceChip() {
  const [balance, setBalance] = useState<number | null>(null);
  const { pathname } = useLocation();

  const load = useCallback(() => {
    api<{ available_balance: number }>('/v1/wallet/balance')
      .then((body) => setBalance(body.available_balance))
      .catch(() => setBalance(null));
  }, []);

  useEffect(() => {
    load();
  }, [load, pathname]);

  useEffect(() => {
    window.addEventListener('aiagg:balance', load);
    return () => window.removeEventListener('aiagg:balance', load);
  }, [load]);

  return (
    <Link to="/wallet" className="balance-chip" title="Saldo tersedia">
      <Icon name="wallet" size={15} />
      <strong>{balance === null ? '—' : idr(balance)}</strong>
    </Link>
  );
}

const PUBLIC_LINKS = [
  { href: '/#model', label: 'Model' },
  { href: '/#harga', label: 'Harga' },
  { href: '/#qris', label: 'Isi saldo' },
  { href: '/#pengembang', label: 'Pengembang' },
];

function PublicDocs() {
  return (
    <SearchProvider mode={{ kind: 'public' }}>
      <div className="public-docs">
        <header className="public-header">
          <div className="public-header-in">
            <a href="/" className="brand public-brand">
              <BrandMark />
            </a>
            <nav className="public-links" aria-label="Utama">
              {PUBLIC_LINKS.map((link) => (
                <a key={link.href} href={link.href}>
                  {link.label}
                </a>
              ))}
              <Link to="/docs" aria-current="page">
                Dokumentasi
              </Link>
            </nav>
            <div className="public-actions">
              <SearchTrigger placeholder="Cari dokumentasi" />
              <SearchTrigger variant="icon" placeholder="Cari dokumentasi" />
              <ThemeToggle />
              <Link className="btn btn-sm public-login" to="/login">
                Masuk
              </Link>
              <Link className="btn btn-sm btn-primary" to="/login?daftar=1">
                Daftar
              </Link>
            </div>
          </div>
        </header>
        <main className="public-main">
          <Docs publicView />
        </main>
        <footer className="public-foot">
          <span>© 2026 {BRAND_NAME}</span>
          <nav aria-label="Tautan bawah">
            <a href="/">Beranda</a>
            <a href="/#model">Model dan harga</a>
            <a href="mailto:halo@partnerai.id">Hubungi kami</a>
          </nav>
        </footer>
      </div>
    </SearchProvider>
  );
}

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [checking, setChecking] = useState(true);
  const drawer = useNavDrawer();
  const crumb = useCrumb();

  const loadSession = useCallback(async () => {
    if (!tokens.access) {
      setSession(null);
      setChecking(false);
      return;
    }
    try {
      setSession(await api<Session>('/v1/auth/me'));
    } catch {
      tokens.clear();
      setSession(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    void loadSession();
    // refresh failed for good
    const onUnauth = () => setSession(null);
    window.addEventListener('aiagg:unauthenticated', onUnauth);
    return () => window.removeEventListener('aiagg:unauthenticated', onUnauth);
  }, [loadSession]);

  if (checking) {
    return (
      <div className="auth-shell">
        <span className="spinner" style={{ width: 22, height: 22 }} />
      </div>
    );
  }

  if (!session) {
    return (
      <Routes>
        <Route path="/docs/:slug?" element={<PublicDocs />} />
        <Route path="*" element={<Login onAuthenticated={loadSession} />} />
      </Routes>
    );
  }

  const signOut = () => {
    tokens.clear();
    window.location.assign('/');
  };

  return (
    <SearchProvider mode={{ kind: 'app', signOut }}>
      <div className={drawer.open ? 'app nav-open' : 'app'}>
        <aside className="sidebar" id="portal-nav" aria-label="Navigasi">
          <Link to="/dashboard" className="brand" onClick={drawer.close}>
            <BrandMark sub="Portal developer" />
          </Link>
          <button className="btn btn-sm btn-ghost icon-btn nav-close" onClick={drawer.close} aria-label="Tutup menu">
            <Icon name="close" size={16} />
          </button>

          {NAV.map((group) => (
            <div key={group.section}>
              <div className="nav-section">{group.section}</div>
              {group.links.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  onClick={drawer.close}
                  className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
                >
                  <Icon name={link.icon} size={17} className="nav-icon" />
                  {link.label}
                </NavLink>
              ))}
            </div>
          ))}

          <div className="sidebar-foot">
            <Link to="/wallet" className="btn btn-primary nav-cta" onClick={drawer.close}>
              <Icon name="plus" size={15} />
              Isi saldo
            </Link>
            <div className="row" style={{ padding: '4px 10px', gap: 10 }}>
              <span className="avatar">{session.email.slice(0, 1).toUpperCase()}</span>
              <span className="truncate grow" style={{ fontSize: 13, color: 'var(--text-secondary)' }} title={session.email}>
                {session.email}
              </span>
            </div>
            <button className="nav-link" onClick={signOut}>
              <Icon name="logout" size={17} className="nav-icon" />
              Keluar
            </button>
          </div>
        </aside>
        {drawer.open && <div className="nav-scrim" onClick={drawer.close} aria-hidden />}

        <div className="main-col">
          <header className="topbar">
            <div className="topbar-lead">
              <button
                className="btn btn-sm icon-btn nav-toggle"
                onClick={drawer.show}
                aria-label="Buka menu"
                aria-controls="portal-nav"
                aria-expanded={drawer.open}
              >
                <Icon name="menu" size={17} />
              </button>
              <Link to="/dashboard" className="topbar-brand" aria-label="Beranda">
                <span className="brand-mark">
                  <MarkGlyph />
                </span>
                {BRAND_NAME}
              </Link>
              <div className="crumbs">
                {crumb.section && <span>{crumb.section}</span>}
                {crumb.section && <span className="sep">/</span>}
                <strong>{crumb.label}</strong>
              </div>
            </div>
            <div className="topbar-search">
              <SearchTrigger />
            </div>
            <div className="topbar-actions">
              <SearchTrigger variant="icon" />
              <DemoBadge />
              <BalanceChip />
              <Link to="/wallet" className="btn btn-sm btn-primary topup-btn">
                <Icon name="plus" size={15} />
                Isi saldo
              </Link>
              <ThemeToggle />
            </div>
          </header>

          <main className="main">
            <Routes>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/playground" element={<Playground />} />
              <Route path="/models" element={<Models />} />
              <Route path="/docs/:slug?" element={<Docs />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/projects/:id" element={<ProjectDetail />} />
              <Route path="/api-keys" element={<ApiKeys />} />
              <Route path="/usage" element={<Usage />} />
              <Route path="/wallet" element={<Wallet />} />
              <Route path="/checkout/:externalId" element={<Checkout />} />
              <Route path="/transactions" element={<Transactions />} />
              <Route path="/settings" element={<Settings session={session} />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </main>
        </div>
      </div>
    </SearchProvider>
  );
}
